import React from 'react'
import Section from '../../common/section/section'
import { MembershipPayment, MembershipPaymentStatus } from 'memberships/schema'
import { formatISOString, DateFormats } from 'utils/dates'
import PermissionRequired from 'admin/permissionRequired'
import Currency from 'uiComponents/money/currency'
import { LoaderWrapper } from 'uiComponents/loaders'
import Badge, { BadgeVariant } from 'uiComponents/badge'
import { t } from 'translations'
import './payments.scss'

const getPaymentStatusMap = (): Record<MembershipPaymentStatus, { text: string; variant: BadgeVariant }> => ({
    [MembershipPaymentStatus.PAID]: { text: t('memberships.membershipDetailsPanel.payments.status.paid'), variant: 'success' },
    [MembershipPaymentStatus.REFUNDED]: { text: t('memberships.membershipDetailsPanel.payments.status.refunded'), variant: 'info' },
    [MembershipPaymentStatus.REFUND_IN_PROGRESS]: { text: t('memberships.membershipDetailsPanel.payments.status.refundInProgress'), variant: 'info' },
    [MembershipPaymentStatus.PENDING]: { text: t('memberships.membershipDetailsPanel.payments.status.pending'), variant: 'warning' },
    [MembershipPaymentStatus.OPEN]: { text: t('memberships.membershipDetailsPanel.payments.status.open'), variant: 'warning' },
    [MembershipPaymentStatus.FAILED]: { text: t('memberships.membershipDetailsPanel.payments.status.failed'), variant: 'error' },
    [MembershipPaymentStatus.CHARGED_BACK]: { text: t('memberships.membershipDetailsPanel.payments.status.chargedBack'), variant: 'error' },
    [MembershipPaymentStatus.CANCELLED]: { text: t('memberships.membershipDetailsPanel.payments.status.cancelled'), variant: 'neutral' },
})

const PaymentStatus = ({ status, loading }: { status: MembershipPaymentStatus; loading?: boolean }) => {
    const { text, variant } = getPaymentStatusMap()[status]

    return (
        <Badge variant={variant} loading={loading}>
            {text}
        </Badge>
    )
}

interface PaymentsSectionProps {
    payments: MembershipPayment[]
    accountSlug: string
    isLoading: boolean
    setPaymentToRefund: (payment: MembershipPayment) => void
}

const PaymentsSection = ({ payments, accountSlug, isLoading, setPaymentToRefund }: PaymentsSectionProps) => {
    const sortedPayments = [...payments].sort(
        (a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime(),
    )

    return (
        <Section title={t('memberships.membershipDetailsPanel.payments.title')} className="membership-details-payments-section">
            <table className="membership-details-payments-table">
                <thead>
                    <tr>
                        <th className="status">{t('memberships.membershipDetailsPanel.payments.columns.status')}</th>
                        <th className="amount">{t('memberships.membershipDetailsPanel.payments.columns.amount')}</th>
                        <th className="date">{t('memberships.membershipDetailsPanel.payments.columns.date')}</th>
                        <th className="action" />
                    </tr>
                </thead>
                <tbody>
                    <LoaderWrapper loading={isLoading}>
                        {sortedPayments.map((payment) => (
                            <tr key={payment.paymentDate}>
                                <td className="status">
                                    <PaymentStatus status={payment.status} loading={isLoading} />
                                </td>
                                <td className="amount">
                                    <Currency amount={payment.amount} currency={payment.currency} />
                                </td>
                                <td className="date">{formatISOString(payment.paymentDate, DateFormats.SHORT_DATE)}</td>
                                <td className="action">
                                    {payment.status === 'PAID' && (
                                        <PermissionRequired name="refund_orders" accountSlug={accountSlug}>
                                            <div className="refund-button" onClick={() => setPaymentToRefund(payment)}>
                                                {t('memberships.membershipDetailsPanel.payments.refund')}
                                            </div>
                                        </PermissionRequired>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </LoaderWrapper>
                </tbody>
            </table>
        </Section>
    )
}

export default PaymentsSection
